  <div class="compliance-checklist">
    <!-- Loading State -->
    <div v-if="loading" class="d-flex flex-column align-items-center justify-content-center text-secondary py-5 min-h-400">
      <div class="spinner-border text-primary" role="status">
        <span class="visually-hidden">Loading...</span>
      </div>
      <p class="mt-3 text-muted mb-0">Loading compliance checklist...</p>
    </div>

    <!-- Form Container -->
    <div v-else-if="form" class="form-container">
      <div v-for="main_section in form" :key="main_section.id" class="main-section-wrapper">
        <div v-for="sub_section in main_section.sub_sections" :key="sub_section.section_id" class="card sub-section-card border-0 shadow-sm overflow-hidden mb-4">
          <!-- Header -->
          <div class="card-header border-0 px-4 py-3 custom-header cursor-pointer"
               @click="toggle_section(sub_section.section_id)">
            <div class="d-flex justify-content-between align-items-center">
              <p class="mb-0 fw-semibold text-white text-capitalize">
                {{ sub_section.section_name }}
              </p>
              <i class="bi text-white"
                 :class="is_section_open(sub_section.section_id) ? 'bi-chevron-up' : 'bi-chevron-down'"></i>
            </div>
          </div>

          <!-- Body -->
          <div :id="`section-${sub_section.section_id}`"
               class="collapse"
               :class="{ show: is_section_open(sub_section.section_id) }">
            <div class="card-body">
              <div v-for="question in sub_section.questions"
                   :key="question.section_question_id"
                   class="bg-light border rounded-3 p-3 p-md-4 mb-3 shadow-sm">

                <div class="d-flex flex-column flex-md-row justify-content-between align-items-stretch align-items-md-start gap-3 mb-3">
                  <div class="flex-grow-1">
                    <p class="mb-0 fw-semibold text-dark text-xs text-capitalize">
                      {{ question.question_text }}
                    </p>
                  </div>
                 <button
                      type="button"
                      class="btn btn-primary-dark btn-sm rounded-small text-xxs shadow"
                      data-bs-toggle="modal"
                      data-bs-target="#assign_questions_modal"
                      @click="set_selected_question(question, sub_section.section_name)"
                    >
                      Assign Target
                    </button>
                </div>

                <!-- Target Questions -->
                <div v-if="question.target_questions && question.target_questions.length > 0" class="border-top pt-3 mt-3">
                  <div class="d-flex align-items-center mb-3">
                    <h6 class="mb-0 text-uppercase small fw-semibold text-secondary">
                      Target Questions
                      <span class="badge bg-primary rounded-pill ms-2">{{ question.target_questions.length }}</span>
                    </h6>
                  </div>

                  <div v-for="(target_q, index) in question.target_questions"
                       :key="target_q.id || index"
                       class="d-flex flex-column flex-md-row gap-3 mb-2 bg-white border rounded-3 p-3 align-items-start">
                    <div class="rounded-circle bg-primary text-white d-inline-flex align-items-center justify-content-center fw-bold flex-shrink-0 badge-circle-28">
                      {{ index + 1 }}
                    </div>
                    <div class="flex-grow-1">
                      <p class="mb-1 small text-body">{{ target_q.question_text }}</p>
                      <div class="mt-2">
                        <span class="badge bg-secondary me-2">{{ format_question_type(target_q.question_type) }}</span>
                        <span v-if="target_q.is_mandatory" class="badge bg-danger">Mandatory</span>
                      </div>
                    </div>
                    <button @click="remove_target_question(question, target_q, index)"
                            class="btn btn-link p-0 text-danger fs-5 lh-1 align-self-end align-self-md-auto"
                            title="Remove target question">
                      <i class="bi bi-x-circle-fill"></i>
                    </button>
                  </div>
                </div>

                <!-- Empty State -->
                <div v-else class="text-center py-3 mt-3 bg-white border border-2 border-dashed rounded-3">
                  <i class="bi bi-inbox text-muted fs-3 d-block mb-2"></i>
                  <p class="text-muted mb-0">No target questions assigned yet</p>
                </div>

              </div>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- Assign Questions Modal -->
    <teleport to="body">
      <div class="modal fade" id="assign_questions_modal" tabindex="-1" aria-labelledby="assign_questions_modal_label" aria-hidden="true">
        <div class="modal-dialog modal-dialog-centered modal-lg">
          <div class="modal-content bg-white rounded-3 p-0 shadow-lg">
            <div class="modal-header border-bottom-0 cute-header">
              <p class="modal-title text-white text-xxs" id="assign_questions_modal_label">Assign Questions</p>
              <button type="button" class="btn-close" data-bs-dismiss="modal" aria-label="Close"></button>
            </div>
            <div class="modal-body py-3">
              <div class="card mb-4 border-0">
                <div class="card-body p-0">
                  <div v-if="is_loading_questions" class="text-center py-4">
                    <div class="spinner-border" role="status"></div>
                    <p class="text-muted mt-2 fs-6">Loading questions...</p>
                  </div>
                  <div v-else>
                    <div class="d-flex justify-content-between align-items-center mb-3">
                      <p class="text-muted mb-0 text-xs">
                        Toggle to assign or unassign <strong>main questions</strong>. Dependent questions are assigned automatically.
                      </p>
                      <div class="input-group w-auto text-xxs">
                        <input type="text" class="form-control form-control-sm text-xxs rounded-lg"
                               placeholder="Search questions..." v-model="question_search" />
                      </div>
                    </div>

                    <div class="table-responsive">
                      <table class="table table-bordered table-hover align-middle">
                        <thead class="table-header text-white">
                          <tr class="text-xs">
                            <th class="text-center">Question</th>
                            <th class="text-center">Type</th>
                            <th class="text-center">Number</th>
                            <th class="text-center">Assign</th>
                          </tr>
                        </thead>
                        <tbody>
                          <template v-for="q in filtered_questions" :key="q.section_question_id">
                            <tr class="text-xxs">
                              <td>
                                <div class="d-flex align-items-start gap-2">
                                  <span class="badge text-xxs rounded-lg"
                                        :class="q.question?.is_assigned_as_dependent ? 'bg-warning text-dark' : 'bg-primary text-white'">
                                    {{ q.question?.is_assigned_as_dependent ? 'Dependent' : 'Main' }}
                                  </span>
                                  <div><div class="fw-medium">{{ q.question?.question_text || 'No question text' }}</div></div>
                                </div>
                              </td>
                              <td>{{ format_question_type(q.question?.question_type) }}</td>
                              <td>{{ q.question?.question_number || '-' }}</td>
                              <td class="text-center">
                                <div class="form-check form-switch d-flex justify-content-center">
                                  <input class="form-check-input" type="checkbox"
                                         :checked="q.is_assigned"
                                         :disabled="q.is_loading"
                                         @change="on_toggle_assign(q)" />
                                </div>
                              </td>
                            </tr>
                            <tr v-for="dq in q.dependent_questions" :key="'dep-' + dq.id" class="bg-light text-xxs">
                              <td class="ps-2">
                                <div class="d-flex align-items-start gap-2">
                                  <span class="badge bg-warning text-dark text-xxs rounded-lg">Dependent</span>
                                  <div><div class="fw-medium">{{ dq.question_text }}</div></div>
                                </div>
                              </td>
                              <td>{{ format_question_type(dq.question_type) }}</td>
                              <td>{{ dq.question_number || '-' }}</td>
                              <td><span class="badge bg-info text-white text-xxs">Auto-assigned</span></td>
                            </tr>
                          </template>

                          <tr v-if="filtered_questions.length === 0">
                            <td colspan="5" class="text-center py-3 text-muted fs-6">No matching questions found</td>
                          </tr>
                        </tbody>
                      </table>
                    </div>
                    <div class="d-flex justify-content-between align-items-center mt-3">
                      <p class="text-muted text-xs">Showing {{ filtered_questions.length }} of {{ question_list.length }} main questions</p>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </teleport>
  </div>